import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../config/api";

function AboutAdmin() {
  const [data, setData] = useState({
    description: "",
    secondDescription: "",
    skills: {
      Frontend: [],
      Backend: [],
      "Machine Learning": [],
      Tools: [],
    },
  });
  const [skillInput, setSkillInput] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Frontend");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [newCategoryInput, setNewCategoryInput] = useState("");
  const [showAddCategory, setShowAddCategory] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API_URL}/about`);
      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching about:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.put(`${API_URL}/about`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setMessage({
          type: "success",
          text: "About section updated successfully!",
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Update failed",
      });
    } finally {
      setLoading(false);
    }
  };

  const addSkill = () => {
    if (
      skillInput.trim() &&
      !data.skills[selectedCategory]?.includes(skillInput.trim())
    ) {
      setData({
        ...data,
        skills: {
          ...data.skills,
          [selectedCategory]: [
            ...(data.skills[selectedCategory] || []),
            skillInput.trim(),
          ],
        },
      });
      setSkillInput("");
    }
  };

  const removeSkill = (category, index) => {
    setData({
      ...data,
      skills: {
        ...data.skills,
        [category]: data.skills[category].filter((_, i) => i !== index),
      },
    });
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <h2 className="text-xl sm:text-2xl font-light mb-4 sm:mb-6">
        Edit About Section
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        <div>
          <label className="block text-white/60 text-xs sm:text-sm mb-2">
            Main Description
          </label>
          <textarea
            value={data.description}
            onChange={(e) => setData({ ...data, description: e.target.value })}
            rows="4"
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/[0.05] border border-white/[0.1] rounded-lg sm:rounded-xl text-white text-sm sm:text-base
                     focus:outline-none focus:border-white/30 transition-all resize-none"
            required
          />
        </div>

        <div>
          <label className="block text-white/60 text-xs sm:text-sm mb-2">
            Second Description (Optional)
          </label>
          <textarea
            value={data.secondDescription}
            onChange={(e) =>
              setData({ ...data, secondDescription: e.target.value })
            }
            rows="4"
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/[0.05] border border-white/[0.1] rounded-lg sm:rounded-xl text-white text-sm sm:text-base
                     focus:outline-none focus:border-white/30 transition-all resize-none"
          />
        </div>

        <div>
          <label className="block text-white/60 text-xs sm:text-sm mb-2">
            Skills by Category
          </label>

          {/* Category Selector */}
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {Object.keys(data.skills).map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 sm:px-4 py-2 rounded-lg transition-all text-xs sm:text-sm ${
                    selectedCategory === category
                      ? "bg-white/20 border border-white/30 text-white"
                      : "bg-white/[0.05] border border-white/[0.1] text-white/60 hover:bg-white/10"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
            <div>
              <button
                type="button"
                onClick={() => setShowAddCategory(!showAddCategory)}
                className="text-xs text-white/40 hover:text-white/60 transition-colors"
              >
                {showAddCategory ? "- Cancel" : "+ Add New Category"}
              </button>
              {showAddCategory && (
                <div className="flex gap-2 mt-2">
                  <input
                    type="text"
                    value={newCategoryInput}
                    onChange={(e) => setNewCategoryInput(e.target.value)}
                    placeholder="New category name (e.g., DevOps)"
                    className="flex-1 px-3 py-2 bg-white/[0.05] border border-white/[0.1] rounded-lg text-white text-sm
                             focus:outline-none focus:border-white/30 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        newCategoryInput.trim() &&
                        !data.skills[newCategoryInput.trim()]
                      ) {
                        setData({
                          ...data,
                          skills: {
                            ...data.skills,
                            [newCategoryInput.trim()]: [],
                          },
                        });
                        setSelectedCategory(newCategoryInput.trim());
                        setNewCategoryInput("");
                        setShowAddCategory(false);
                      }
                    }}
                    className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition-all text-xs"
                  >
                    Add
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Add Skill Input */}
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <input
              type="text"
              value={skillInput}
              onChange={(e) => setSkillInput(e.target.value)}
              onKeyPress={(e) =>
                e.key === "Enter" && (e.preventDefault(), addSkill())
              }
              className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 bg-white/[0.05] border border-white/[0.1] rounded-lg sm:rounded-xl text-white text-sm sm:text-base
                       focus:outline-none focus:border-white/30 transition-all"
              placeholder={`Add skill to ${selectedCategory}`}
            />
            <button
              type="button"
              onClick={addSkill}
              className="px-4 sm:px-6 py-2.5 sm:py-3 bg-white/10 border border-white/20 rounded-lg sm:rounded-xl hover:bg-white/20 transition-all text-xs sm:text-sm whitespace-nowrap"
            >
              Add to {selectedCategory}
            </button>
          </div>

          {/* Skills Display by Category */}
          <div className="space-y-4">
            {Object.entries(data.skills).map(([category, skills]) => (
              <div
                key={category}
                className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4"
              >
                <h3 className="text-white/70 text-sm font-medium mb-3">
                  {category}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {skills && skills.length > 0 ? (
                    skills.map((skill, index) => (
                      <div
                        key={index}
                        className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg flex items-center gap-2"
                      >
                        <span className="text-white/80 text-sm">{skill}</span>
                        <button
                          type="button"
                          onClick={() => removeSkill(category, index)}
                          className="text-red-400 hover:text-red-300 text-lg font-bold"
                        >
                          ×
                        </button>
                      </div>
                    ))
                  ) : (
                    <span className="text-white/30 text-sm">
                      No skills added yet
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {message.text && (
          <div
            className={`p-4 rounded-lg ${
              message.type === "success"
                ? "bg-green-500/10 border border-green-500/20 text-green-400"
                : "bg-red-500/10 border border-red-500/20 text-red-400"
            }`}
          >
            {message.text}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-2.5 sm:py-3 bg-white/10 border border-white/20 rounded-lg sm:rounded-xl hover:bg-white/20 
                   transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
        >
          {loading ? "Updating..." : "Update About"}
        </button>
      </form>
    </div>
  );
}

export default AboutAdmin;
